import { useState, useRef, useCallback, useEffect } from "react";
import { products } from "salesive-api-axios";

/**
 * Hook for @product mention functionality in chat input.
 *
 * Detects "@" in input, extracts the search query after it,
 * fetches matching products, and provides state for the suggestions dropdown.
 */
export function useProductMention({ inputValue, setInputValue, inputRef, enabled = true }) {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    // Track the position of the "@" trigger in the input
    const mentionStartRef = useRef(null);
    const debounceRef = useRef(null);
    const abortRef = useRef(null);

    /**
     * Call this from handleInputChange. Detects "@" trigger and extracts query.
     */
    const onInputChange = useCallback(
        (newValue) => {
            if (!enabled) return;
            const textarea = inputRef?.current;
            if (!textarea) return;

            // Use selectionStart to find cursor position (will be set after state update)
            // We need to schedule this check after React updates the DOM
            requestAnimationFrame(() => {
                const cursorPos = textarea.selectionStart;
                // Look backwards from cursor for "@"
                const textBeforeCursor = newValue.slice(0, cursorPos);
                const lastAtIndex = textBeforeCursor.lastIndexOf("@");

                if (lastAtIndex === -1) {
                    close();
                    return;
                }

                // Ensure "@" is at start or preceded by a space/newline
                if (
                    lastAtIndex > 0 &&
                    !/\s/.test(newValue[lastAtIndex - 1])
                ) {
                    close();
                    return;
                }

                // Check there's no space-break after the query that would end the mention
                const textAfterAt = newValue.slice(
                    lastAtIndex + 1,
                    cursorPos
                );

                // Allow spaces within product names - only close if cursor moved before @
                if (cursorPos <= lastAtIndex) {
                    close();
                    return;
                }

                mentionStartRef.current = lastAtIndex;
                setQuery(textAfterAt);
                setIsOpen(true);
                setActiveIndex(0);
                debouncedSearch(textAfterAt);
            });
        },
        [inputRef, enabled]
    );

    const debouncedSearch = useCallback((q) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (abortRef.current) abortRef.current.abort?.();

        if (!q.trim()) {
            setResults([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const controller = new AbortController();
                abortRef.current = controller;
                const response = await products.list({
                    q: q.trim(),
                    limit: 6,
                });
                if (!controller.signal.aborted) {
                    setResults(response.data?.data?.products || []);
                }
            } catch (err) {
                if (err?.name !== "CanceledError") {
                    console.error("[ProductMention] search error:", err);
                    setResults([]);
                }
            } finally {
                setIsLoading(false);
            }
        }, 300);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
        setQuery("");
        setResults([]);
        setActiveIndex(0);
        mentionStartRef.current = null;
        if (debounceRef.current) clearTimeout(debounceRef.current);
    }, []);

    /**
     * Select a product - replace @query with the product name
     */
    const selectProduct = useCallback(
        (product) => {
            const start = mentionStartRef.current;
            if (start === null) return;

            const textarea = inputRef?.current;
            const cursorPos = textarea?.selectionStart ?? inputValue.length;

            const before = inputValue.slice(0, start);
            const after = inputValue.slice(cursorPos);
            const newValue = before + product.name + " " + after;

            setInputValue(newValue);
            close();

            // Restore focus and cursor position
            requestAnimationFrame(() => {
                if (textarea) {
                    textarea.focus();
                    const newCursorPos = before.length + product.name.length + 1;
                    textarea.setSelectionRange(newCursorPos, newCursorPos);
                }
            });
        },
        [inputValue, setInputValue, inputRef, close]
    );

    /**
     * Handle keyboard navigation in the suggestions dropdown.
     * Returns true if the event was handled (caller should preventDefault).
     */
    const onKeyDown = useCallback(
        (e) => {
            if (!isOpen || results.length === 0) return false;

            if (e.key === "ArrowDown") {
                setActiveIndex((prev) =>
                    prev < results.length - 1 ? prev + 1 : 0
                );
                return true;
            }
            if (e.key === "ArrowUp") {
                setActiveIndex((prev) =>
                    prev > 0 ? prev - 1 : results.length - 1
                );
                return true;
            }
            if (e.key === "Enter" && !e.shiftKey) {
                selectProduct(results[activeIndex]);
                return true;
            }
            if (e.key === "Escape") {
                close();
                return true;
            }
            return false;
        },
        [isOpen, results, activeIndex, selectProduct, close]
    );

    // Close dropdown when disabled
    useEffect(() => {
        if (!enabled) close();
    }, [enabled, close]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    return {
        isOpen,
        query,
        results,
        isLoading,
        activeIndex,
        onInputChange,
        onKeyDown,
        selectProduct,
        close,
    };
}
